import styles from "~/pages/blog/blog-index.module.scss";
import ArticleCard from "~/components/Blog/ArticleCard";
import Layout from "~/components/layouts/Blog";
import Head from "~/components/Meta/Head";
import {
  getPost,
  getFeaturedPosts,
  getTag,
  getCanonicalUrl,
  getPosts,
  formatBlogPosts,
} from "~/utils/Blog/Post";
import { FetchError } from "~/utils/Fetch/json";
import { blog as blogConfig } from "~/config/blog";
import Article from "~/components/Blog/Article";
import Tags from "~/components/Blog/Tags";
import Author from "~/components/Blog/Author";
import CTA from "~/components/Common/CTA";
import { BLOG_FUNNEL } from "~/config/funnel";
import Blog from "@referralexchange/rea-ui-react/src/components/blocks/Blog";

const SinglePost = ({ featured, post, tag, blog_posts, meta_title }) => {
  const canonicalUrl = getCanonicalUrl(post, "real-estate-guides");

  return (
    <>
      <Head
        title={meta_title}
        description={post?.meta_description}
        socialImageUrl={post?.og_image || post?.twitter_image}
        url={canonicalUrl}
        breadcrumbJson={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          url: canonicalUrl,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          keywords: post.tags
            .filter((tag) => tag.visibility == "public")
            .map((tag) => {
              return tag.name;
            })
            .join(", "),
          description: post?.meta_description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "RealEstateAgents.com",
            url: "https://www.realestateagents.com/",
            logo: {
              "@type": "ImageObject",
              url:
                "https://www.realestateagents.com/static/images/logos/rea-icon-color.2x.min.png",
            },
          },
          author: {
            "@type": "Person",
            name: post?.primary_author?.name,
            url: post?.primary_author?.url,
            image: {
              "@type": "ImageObject",
              url: post?.primary_author?.profile_image,
            },
          },
          image: {
            "@type": "ImageObject",
            url: post?.feature_image,
          },
        }}
      />
      <div className={styles["blog-index"]}>
        <div className={styles["blog-index__container"]}>
          <main className={styles["blog-index__single"]}>
            <Article post={post} />
            <Tags post={post} />
            <Author post={post} />
          </main>
          <aside className={styles["blog-index__sidebar"]}>
            <div className={styles["blog-index__sidebar-container"]}>
              <div className={styles["blog-index__cta-container"]}>
                <CTA page={"blog"} funnel={BLOG_FUNNEL} />
              </div>
              <div className={styles["blog-index__sidebar-heading"]}>
                Featured Blog Posts
              </div>
              {featured.posts.map((post, key) => {
                return <ArticleCard minimal={true} post={post} key={key} />;
              })}
            </div>
          </aside>
        </div>
        {blog_posts && blog_posts.length > 0 && (
          <div className={styles["blog-index__more-posts-container"]}>
            <Blog
              title={`More Resources`}
              posts={blog_posts}
              button={{
                label: "View More",
                href: `${blogConfig.guidesUrl}`,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

SinglePost.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export const getStaticProps = async (ctx) => {
  const { tag, slug } = ctx.params;
  const tag_decode = decodeURI(tag);
  const slug_decode = decodeURI(slug);
  const has_brackets_regex = new RegExp(/^[\[\]]/);
  if (has_brackets_regex.test(slug_decode)) {
    return {
      props: { statusCode: 404 },
      notFound: true,
    };
  }

  try {
    const [post_res, featured_res] = await Promise.all([
      getPost({ slug: slug_decode }),
      getFeaturedPosts({}),
    ]);

    const [post, featured] = await Promise.all([
      post_res.data.posts[0],
      featured_res.data,
    ]);

    const tag = getTag(post);

    if (post_res?.data?.posts.length <= 0) {
      return {
        props: { statusCode: 404 },
        notFound: true,
        revalidate: 60 * 5,
      };
    }

    const filters = `id:-${post.id}${tag?.slug ? `+tag:${tag.slug}` : ""}`;
    const [posts_res] = await Promise.all([
      getPosts({ filters, pageLimit: 6 }),
    ]);
    const [ghost] = await Promise.all([posts_res.data]);
    const blog_posts = formatBlogPosts(ghost.posts);

    const meta_title = post.meta_title;
    const max_length = 47; // Max title tag length of 70 but that also includes ` | Real EstateAgents.com`(23 length) 70 - 23 = 47
    const trimmed_title =
      meta_title.length > max_length
        ? meta_title.substr(0, max_length - 3) + "... | RealEstateAgents.com"
        : meta_title + " | RealEstateAgents.com";

    return {
      props: { featured, post, blog_posts, tag, meta_title: trimmed_title },
    };
  } catch (error) {
    if (
      error instanceof FetchError &&
      (error.response.status === 404 || error.response.status == 422)
    ) {
      return {
        props: { statusCode: 404 },
        notFound: true,
        revalidate: 60 * 5,
      };
    } else {
      throw error; // let others bubble up
    }
  }
};

export default SinglePost;
